<template>
  <div id="jurisdictionsMap" />
</template>

<script>
import { mapState, mapGetters, mapMutations } from 'vuex';

import maplibregl from 'maplibre-gl';
import jurisdictionMapOptions from '@/app/jurisdiction/data/jurisdictionMapOptions.js';

export default {
  name: 'JuridictionMap',

  data() {
    return {
      hoveredCommune: null
    };
  },

  computed: {
    ...mapState('municipalities', [
      'map',
      'municipalitiesList',
      'selectedMunicipalities'
    ]),
    ...mapGetters('municipalities', [
      'mapBounds'
    ]),
    ...mapGetters('jurisdiction', [
      'currentJurisdictionBounds'
    ])
  },

  watch: {
    hoveredCommune: {
      deep: true,
      handler(newValue, oldValue) {
        if (oldValue) {
          this.map.setFeatureState(oldValue, {
            ...this.map.getFeatureState(oldValue),
            hover: false,
          });
        }
        if (newValue) {
          this.map.setFeatureState(newValue, {
            ...this.map.getFeatureState(newValue),
            hover: true,
          });
        }
      }
    },
    selectedMunicipalities: {
      deep: true,
      handler(newValue) {
        for (const insee of newValue.map(el => el.insee)) {
          this.map.setFeatureState(
            {
              source: 'decoupage',
              sourceLayer: 'commune',
              id: insee,
            },
            {
              select: true,
            }
          );
        }
      }
    },
    currentJurisdictionBounds: {
      deep: true,
      handler(newValue) {
        if (newValue && this.$router.currentRoute.name !== 'NewJurisdiction') {
          try {
            this.map.fitBounds(newValue, { padding: 10 });
            this.map.once('moveend', () => {
              this.checkIfJurisdictionInBounds();
            });
          } catch (err) {
            console.error(err);
          }
        }
      }
    }
  },

  mounted() {
    try {
      this.SET_MUNICIPALITIES_MAP(
        new maplibregl.Map({
          container: 'jurisdictionsMap',
          pixelRatio: 2.0,
          ...jurisdictionMapOptions,
        })
      );
      this.map.once('load', () => {
        const INSEEs = this.municipalitiesList.map(el => el.insee);
        this.map.setFilter('communes', ['in', ['get', 'code'], ['literal', INSEEs]]);
        this.map.setFilter('commune_limites', ['in', ['get', 'code'], ['literal', INSEEs]]);
        this.map.setFilter('commune_labels', ['in', ['get', 'code'], ['literal', INSEEs]]);
        this.map.on('mousemove', 'communes', (evt) => {
          this.hoverCommune(evt.point);
        });
        this.map.on('mouseleave', 'communes', () => {
          this.hoveredCommune = null;
        });
        this.map.on('click', 'communes', (evt) => {
          this.selectCommune(evt.point);
        });
        if (this.currentJurisdictionBounds && this.$router.currentRoute.name !== 'NewJurisdiction') {
          try {
            this.map.fitBounds(this.currentJurisdictionBounds, { padding: 10 });
            this.map.once('moveend', () => {
              this.checkIfJurisdictionInBounds();
            });
          } catch (err) {
            console.error(err);
          }
        } else {
          this.map.fitBounds(
            process.env.VUE_APP_DEFAULT_MAP_BOUNDS ?
              process.env.VUE_APP_DEFAULT_MAP_BOUNDS.split(',') :
              [[-4.965820, 42.261049], [8.569336, 51.248163]]
          );
        }
      });
      this.map.on('sourcedata', 'communes', (evt) => {
        if (evt.isSourceLoaded) {
          const features = this.map.queryRenderedFeatures(this.mapBounds, { layers: ['communes'] });
          for (const feature of features) {
            const foundSelected = this.selectedMunicipalities.find(el => el.insee === feature.id);
            if (!foundSelected) {
              const src = {
                source: feature.source,
                sourceLayer: feature.sourceLayer,
                id: feature.id,
              };
              this.map.setFeatureState(src, {
                select: false,
              });
            }
          }
          for (const municipality of this.selectedMunicipalities) {
            const feature = features.find(el => el.id === municipality.insee);
            if (feature) {
              const src = {
                source: feature.source,
                sourceLayer: feature.sourceLayer,
                id: feature.id,
              };
              this.map.setFeatureState(src, {
                select: true,
              });
            }
          }
        }
      });
    } catch (err) {
      console.error(err);
    }
  },

  methods: {
    ...mapMutations('municipalities', [
      'SET_MUNICIPALITIES_MAP',
      'ADD_SELECTED_MUNICIPALITY',
      'REMOVE_SELECTED_MUNICIPALITY'
    ]),

    hoverCommune(point) {
      const features = this.map.queryRenderedFeatures(point, { layers: ['communes'] });
      if (features && features.length) {
        const src = {
          source: features[0].source,
          sourceLayer: features[0].sourceLayer,
          id: features[0].id,
        };
        const currentFeatureState = this.map.getFeatureState(src);
        if (!currentFeatureState.select) {
          this.hoveredCommune = src;
        } else {
          this.hoveredCommune = null;
        }
      }
    },
    selectCommune(point) {
      const features = this.map.queryRenderedFeatures(point, { layers: ['communes'] });
      if (features && features.length) {
        const src = {
          source: features[0].source,
          sourceLayer: features[0].sourceLayer,
          id: features[0].id,
        };
        const currentFeatureState = this.map.getFeatureState(src);
        if (currentFeatureState.select) {
          this.REMOVE_SELECTED_MUNICIPALITY({
            insee: features[0].properties.code,
            displayName: features[0].properties.nom
          });
        } else {
          this.hoveredCommune = null;
          this.ADD_SELECTED_MUNICIPALITY({
            insee: features[0].properties.code,
            displayName: features[0].properties.nom
          });
        }
        this.map.setFeatureState(src, {
          ...currentFeatureState,
          select: currentFeatureState.select ? false : true,
        });
      }
    },

    checkIfJurisdictionInBounds() {
      const currentMapBounds = this.map.getBounds();
      const jurisdictionCenter = new maplibregl.LngLatBounds(this.currentJurisdictionBounds).getCenter();
      const isInBounds = currentMapBounds.contains(jurisdictionCenter);
      if (!isInBounds) {
        this.map.fitBounds(
          process.env.VUE_APP_DEFAULT_MAP_BOUNDS ?
            process.env.VUE_APP_DEFAULT_MAP_BOUNDS.split(',') :
            [[-4.965820, 42.261049], [8.569336, 51.248163]]
        );
      }
    }
  }
};
</script>

<style lang="less" scoped>
#jurisdictionsMap {
  height: 100%;
  min-height: 630px;
}
</style>